import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/lib/LangContext";
import { http } from "@/lib/api";
import { TIME_SLOTS } from "@/lib/i18n";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { es as esLocale, enUS } from "date-fns/locale";
import { CalendarIcon, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

const DEVICE_TYPES = [
  { value: "phone", key: "phone" },
  { value: "tablet", key: "tablet" },
  { value: "computer", key: "computer" },
  { value: "console", key: "console" },
];

export default function Booking() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [bookedSlots, setBookedSlots] = useState([]);

  const [form, setForm] = useState({
    device_type: "",
    device_brand: "",
    device_model: "",
    issue_description: "",
    service_type: "at_home",
    address: "",
    appointment_date: null,
    appointment_time: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!form.appointment_date) {
      setBookedSlots([]);
      return;
    }
    const dateStr = format(form.appointment_date, "yyyy-MM-dd");
    http.get(`/availability?date=${dateStr}`).then((r) => {
      setBookedSlots(r.data.booked_slots || []);
    }).catch(() => setBookedSlots([]));
  }, [form.appointment_date]);

  const canSubmit =
    form.device_type &&
    form.device_brand &&
    form.device_model &&
    form.issue_description &&
    form.appointment_date &&
    form.appointment_time &&
    form.customer_name &&
    form.customer_email &&
    form.customer_phone &&
    (form.service_type !== "at_home" || form.address);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) {
      toast.error(lang === "es" ? "Completa todos los campos requeridos" : "Please complete all required fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        appointment_date: format(form.appointment_date, "yyyy-MM-dd"),
        language: lang,
      };
      const r = await http.post("/appointments", payload);
      toast.success(t.booking.success_title, { description: t.booking.success_desc });
      navigate(`/confirmation/${r.data.id}`, { state: { appt: r.data } });
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          (lang === "es" ? "Error al agendar. Intenta de nuevo." : "Booking failed. Try again."),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header active="book" />
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-12 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <aside className="lg:col-span-4">
            <div className="tick-label">01 / 01</div>
            <h1 className="font-display text-4xl md:text-5xl tracking-tighter leading-[0.95] mt-4">
              {t.booking.title}
            </h1>
            <p className="text-neutral-600 mt-4 leading-relaxed">{t.booking.subtitle}</p>
            <div className="mt-8 hairline p-6 bg-white text-sm">
              <div className="tick-label mb-3">{t.booking.language_note}</div>
              <div className="font-mono-tight">{lang.toUpperCase()}</div>
            </div>
          </aside>

          <form
            onSubmit={handleSubmit}
            className="lg:col-span-8 hairline bg-white p-8 md:p-10 space-y-8"
            data-testid="booking-form"
          >
            {/* Device */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-2">
                <Label>{t.booking.device_type} *</Label>
                <Select value={form.device_type} onValueChange={(v) => update("device_type", v)}>
                  <SelectTrigger data-testid="input-device-type" className="rounded-none">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {DEVICE_TYPES.map((d) => (
                      <SelectItem key={d.value} value={d.value} data-testid={`option-device-${d.value}`}>
                        {t.devices[d.key]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t.booking.device_brand} *</Label>
                <Input
                  data-testid="input-brand"
                  className="rounded-none"
                  value={form.device_brand}
                  onChange={(e) => update("device_brand", e.target.value)}
                  placeholder="Apple, Samsung…"
                  translate="no"
                />
              </div>
              <div className="space-y-2">
                <Label>{t.booking.device_model} *</Label>
                <Input
                  data-testid="input-model"
                  className="rounded-none"
                  value={form.device_model}
                  onChange={(e) => update("device_model", e.target.value)}
                  placeholder="iPhone 14, PS5…"
                />
              </div>
            </div>

            {/* Issue */}
            <div className="space-y-2">
              <Label>{t.booking.issue} *</Label>
              <Textarea
                data-testid="input-issue"
                className="rounded-none min-h-[120px]"
                value={form.issue_description}
                onChange={(e) => update("issue_description", e.target.value)}
                placeholder={t.booking.issue_ph}
              />
            </div>

            {/* Service type */}
            <div className="space-y-3">
              <Label>{t.booking.service_type} *</Label>
              <RadioGroup
                value={form.service_type}
                onValueChange={(v) => update("service_type", v)}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                <label
                  htmlFor="svc-home"
                  className={`hairline p-4 cursor-pointer flex items-center gap-3 ${form.service_type === "at_home" ? "hairline-strong bg-neutral-50" : ""}`}
                  data-testid="svc-at-home"
                >
                  <RadioGroupItem value="at_home" id="svc-home" />
                  <div>
                    <div className="font-semibold">{t.booking.at_home}</div>
                    <div className="tick-label mt-1">
                      {lang === "es" ? "Vamos a ti" : "We come to you"}
                    </div>
                  </div>
                </label>
                <label
                  htmlFor="svc-shop"
                  className={`hairline p-4 cursor-pointer flex items-center gap-3 ${form.service_type === "in_shop" ? "hairline-strong bg-neutral-50" : ""}`}
                  data-testid="svc-in-shop"
                >
                  <RadioGroupItem value="in_shop" id="svc-shop" />
                  <div>
                    <div className="font-semibold">{t.booking.in_shop}</div>
                    <div className="tick-label mt-1">
                      {lang === "es" ? "En el taller" : "At the shop"}
                    </div>
                  </div>
                </label>
              </RadioGroup>
            </div>

            {form.service_type === "at_home" && (
              <div className="space-y-2">
                <Label>{t.booking.address} *</Label>
                <Input
                  data-testid="input-address"
                  className="rounded-none"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  placeholder={t.booking.address_ph}
                />
              </div>
            )}

            {/* Date & Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label>{t.booking.date} *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="input-date"
                      className="w-full justify-start rounded-none font-normal"
                    >
                      <CalendarIcon size={16} className="mr-2" />
                      {form.appointment_date
                        ? format(form.appointment_date, "PPP", { locale: lang === "es" ? esLocale : enUS })
                        : t.booking.pick_date}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 bg-white" align="start" data-testid="date-popover">
                    <Calendar
                      mode="single"
                      selected={form.appointment_date}
                      onSelect={(d) => update("appointment_date", d)}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      locale={lang === "es" ? esLocale : enUS}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>{t.booking.time} *</Label>
                <Select value={form.appointment_time} onValueChange={(v) => update("appointment_time", v)}>
                  <SelectTrigger data-testid="input-time" className="rounded-none">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map((slot) => {
                      const disabled = bookedSlots.includes(slot);
                      return (
                        <SelectItem
                          key={slot}
                          value={slot}
                          disabled={disabled}
                          data-testid={`option-time-${slot}`}
                        >
                          {slot} {disabled && (lang === "es" ? "· Ocupado" : "· Taken")}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Contact */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-2">
                <Label>{t.booking.name} *</Label>
                <Input
                  data-testid="input-name"
                  className="rounded-none"
                  value={form.customer_name}
                  onChange={(e) => update("customer_name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.booking.email} *</Label>
                <Input
                  data-testid="input-email"
                  type="email"
                  className="rounded-none"
                  value={form.customer_email}
                  onChange={(e) => update("customer_email", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>{t.booking.phone} *</Label>
                <Input
                  data-testid="input-phone"
                  className="rounded-none"
                  value={form.customer_phone}
                  onChange={(e) => update("customer_phone", e.target.value)}
                />
              </div>
            </div>

            <div className="pt-4 border-t border-neutral-200 flex items-center justify-end">
              <button
                type="submit"
                disabled={submitting}
                data-testid="submit-booking"
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t.booking.submitting : t.booking.submit}
                <ArrowUpRight size={18} />
              </button>
            </div>
          </form>
        </div>
      </section>
      <Footer />
    </div>
  );
}
